/**
 * `lazy()` that survives the one dynamic-import failure a stale tab causes.
 *
 * After a deploy the old chunk URL is gone, the import rejects with a fetch `TypeError`, and a
 * single retry picks up the new manifest. Every lazily loaded surface in the app reaches for this
 * rather than bare `lazy()` — the route views, the hand-built root-folder landings, and the PDF
 * viewer, whose chunk is the largest of them and the one a stale tab is most likely to miss.
 */

import { type ComponentType, type LazyExoticComponent, lazy } from "react";

export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importer();
    } catch (error) {
      const isDynamicImportFetchError =
        error instanceof TypeError &&
        error.message.includes("Failed to fetch dynamically imported module");
      if (!isDynamicImportFetchError) {
        throw error;
      }
      await new Promise<void>((resolve) => {
        setTimeout(resolve, 200);
      });
      return importer();
    }
  });
}
